const LINKLIST_TARGET_ATTR = 'data-linklist-target';
const SCROLL_THROTTLE_MS = 50;

function throttle(fn, wait) {
  let last = 0;
  let timeout = null;

  return (...args) => {
    const now = Date.now();
    const remaining = wait - (now - last);

    if (remaining <= 0) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }

      last = now;
      fn(...args);
    } else if (!timeout) {
      timeout = window.setTimeout(() => {
        last = Date.now();
        timeout = null;
        fn(...args);
      }, remaining);
    }
  };
}

class LinklistMenu extends HTMLElement {
  #scrollListener = null;
  #resizeListener = null;
  #targetByName = new Map();
  #activeTarget = null;
  #pendingActiveUpdate = null;
  #pinnedTarget = null;
  #pinReleaseTimer = null;
  #scrollIntentName = null;
  #scrollIntentUntil = 0;
  #cachedStickyOffset = null;
  #scrollSettleFrame = null;
  #sectionLifecycleListener = null;
  #documentSectionListener = null;
  #tablistKeydownListener = null;
  #heightResizeObserver = null;
  #hashChangeListener = null;
  #popoverId = null;
  #popoverShowListener = null;
  #popoverClickListener = null;

  connectedCallback() {
    this.#popoverId = this.querySelector('.linklist-menu__toggle')?.getAttribute('aria-controls') ?? null;
    this.#bindPopoverSync();
    this.#applyRootStickyVariables();
    this.#observeLinklistHeight();
    this.addEventListener('click', this.#onLinkClick);
    this.#initKeyboardNavigation();
    this.#initScrollSpy({ scrollToHash: true });
    this.#bindLifecycleListeners();

    requestAnimationFrame(() => {
      if (this.#targetByName.size) {
        this.#updateActive();
      }
    });
  }

  disconnectedCallback() {
    this.#teardownScrollSpy();
    this.#removeLifecycleListeners();
    this.#teardownKeyboardNavigation();
    this.#teardownPopoverSync();
    this.#heightResizeObserver?.disconnect();
    this.#heightResizeObserver = null;

    if (this.#pendingActiveUpdate) {
      cancelAnimationFrame(this.#pendingActiveUpdate);
      this.#pendingActiveUpdate = null;
    }

    this.#clearPinnedTarget();
    this.#clearScrollIntent();
    this.#clearRootStickyVariables();
  }

  #bindLifecycleListeners() {
    this.#resizeListener = () => this.#invalidateStickyOffset();
    window.addEventListener('resize', this.#resizeListener, { passive: true });

    const section = this.closest('.shopify-section');

    if (section) {
      this.#sectionLifecycleListener = () => {
        this.#applyRootStickyVariables();
        this.#invalidateStickyOffset();
        this.#teardownScrollSpy();
        this.#initScrollSpy({ scrollToHash: false });
      };

      section.addEventListener('shopify:section:load', this.#sectionLifecycleListener);
      section.addEventListener('shopify:section:select', this.#sectionLifecycleListener);
    }

    this.#documentSectionListener = () => {
      this.#invalidateStickyOffset();
      this.#teardownScrollSpy();
      this.#initScrollSpy({ scrollToHash: false });
    };

    document.addEventListener('shopify:section:load', this.#documentSectionListener);
    document.addEventListener('shopify:section:reorder', this.#documentSectionListener);
  }

  #removeLifecycleListeners() {
    if (this.#resizeListener) {
      window.removeEventListener('resize', this.#resizeListener);
      this.#resizeListener = null;
    }

    const section = this.closest('.shopify-section');

    if (section && this.#sectionLifecycleListener) {
      section.removeEventListener('shopify:section:load', this.#sectionLifecycleListener);
      section.removeEventListener('shopify:section:select', this.#sectionLifecycleListener);
      this.#sectionLifecycleListener = null;
    }

    if (this.#documentSectionListener) {
      document.removeEventListener('shopify:section:load', this.#documentSectionListener);
      document.removeEventListener('shopify:section:reorder', this.#documentSectionListener);
      this.#documentSectionListener = null;
    }
  }

  #normalizeAnchorName(name) {
    return name?.trim().toLowerCase() ?? '';
  }

  #getPopover() {
    return this.#popoverId ? document.getElementById(this.#popoverId) : null;
  }

  #getDesktopAnchorLinks() {
    return [...this.querySelectorAll('.linklist-menu__desktop a[data-linklist-anchor]')];
  }

  #getPopoverAnchorLinks() {
    const popover = this.#getPopover();

    return popover ? [...popover.querySelectorAll('a[data-linklist-anchor]')] : [];
  }

  #getAllAnchorLinks() {
    return [...this.#getDesktopAnchorLinks(), ...this.#getPopoverAnchorLinks()];
  }

  #getPopoverPageLinks() {
    const popover = this.#getPopover();

    return popover ? [...popover.querySelectorAll('.popover__value-option:not([data-linklist-anchor])')] : [];
  }

  #bindPopoverSync() {
    const popover = this.#getPopover();

    if (!popover) {
      return;
    }

    this.#popoverShowListener = () => {
      if (this.#activeTarget) {
        this.#setActive(this.#activeTarget, { force: true });
      } else {
        this.#updateActive();
      }
    };

    popover.addEventListener('dialog:before-show', this.#popoverShowListener);

    this.#popoverClickListener = (event) => this.#onLinkClick(event);
    popover.addEventListener('click', this.#popoverClickListener);
  }

  #teardownPopoverSync() {
    const popover = this.#getPopover();

    if (popover) {
      if (this.#popoverShowListener) {
        popover.removeEventListener('dialog:before-show', this.#popoverShowListener);
        this.#popoverShowListener = null;
      }

      if (this.#popoverClickListener) {
        popover.removeEventListener('click', this.#popoverClickListener);
        this.#popoverClickListener = null;
      }
    }
  }

  #applyRootStickyVariables() {
    const section = this.closest('.shopify-section--linklist');

    if (!section) {
      return;
    }

    const isSticky = getComputedStyle(section).getPropertyValue('--linklist-is-sticky').trim() === '1';
    document.documentElement.style.setProperty('--linklist-is-sticky', isSticky ? '1' : '0');
  }

  #clearRootStickyVariables() {
    if (!document.querySelector('linklist-menu')) {
      document.documentElement.style.removeProperty('--linklist-is-sticky');
    }
  }

  #observeLinklistHeight() {
    const heightObserver = this.closest('.shopify-section--linklist')?.querySelector('height-observer');

    if (!heightObserver) {
      return;
    }

    this.#heightResizeObserver = new ResizeObserver(() => this.#invalidateStickyOffset());
    this.#heightResizeObserver.observe(heightObserver);
  }

  #invalidateStickyOffset() {
    this.#cachedStickyOffset = null;
  }

  #findScrollTarget(hash) {
    const targetName = this.#normalizeAnchorName(decodeURIComponent(hash.slice(1)));

    if (!targetName) {
      return null;
    }

    return (
      this.#targetByName.get(targetName) ??
      document.querySelector(`[${LINKLIST_TARGET_ATTR}="${CSS.escape(targetName)}"]`) ??
      document.querySelector(hash)
    );
  }

  #warnMissingTargets(links) {
    const isDesignMode =
      document.documentElement.classList.contains('shopify-design-mode') || Boolean(window.Shopify?.designMode);

    if (!isDesignMode) {
      return;
    }

    for (const link of links) {
      const name = this.#normalizeAnchorName(link.dataset.linklistAnchor);

      if (!name || this.#targetByName.has(name)) {
        continue;
      }

      console.warn(
        `[linklist-menu] Menu link "#${name}" has no matching [${LINKLIST_TARGET_ATTR}] on the page.`,
        link
      );
    }
  }

  #initScrollSpy({ scrollToHash = false } = {}) {
    const links = this.#getDesktopAnchorLinks();

    if (!links.length) {
      return;
    }

    this.#targetByName.clear();

    for (const link of links) {
      const name = this.#normalizeAnchorName(link.dataset.linklistAnchor);

      if (!name || this.#targetByName.has(name)) {
        continue;
      }

      const target = document.querySelector(`[${LINKLIST_TARGET_ATTR}="${CSS.escape(name)}"]`);

      if (!target) {
        continue;
      }

      this.#targetByName.set(name, target);
    }

    this.#warnMissingTargets(links);

    if (!this.#targetByName.size) {
      return;
    }

    this.#scrollListener = throttle(() => this.#scheduleUpdateActive(), SCROLL_THROTTLE_MS);
    window.addEventListener('scroll', this.#scrollListener, { passive: true });

    this.#updateActive();

    if (scrollToHash) {
      this.#navigateToHash({ behavior: 'auto' });
    }

    if (!this.#hashChangeListener) {
      this.#hashChangeListener = this.#onHashChange;
      window.addEventListener('hashchange', this.#hashChangeListener);
    }
  }

  #teardownScrollSpy() {
    if (this.#scrollListener) {
      window.removeEventListener('scroll', this.#scrollListener);
      this.#scrollListener = null;
    }

    if (this.#hashChangeListener) {
      window.removeEventListener('hashchange', this.#hashChangeListener);
      this.#hashChangeListener = null;
    }

    this.#targetByName.clear();
  }

  #onHashChange = () => {
    this.#navigateToHash({ behavior: 'auto' });
  };

  #navigateToHash({ behavior = 'auto' } = {}) {
    const hash = window.location.hash;

    if (!hash || hash === '#') {
      return;
    }

    const name = this.#normalizeAnchorName(decodeURIComponent(hash.slice(1)));

    if (!this.#targetByName.has(name)) {
      return;
    }

    const target = this.#targetByName.get(name);

    this.#setScrollIntent(name);
    this.#pinTarget(name);
    this.#setActive(name, { force: true });
    this.#scrollToTarget(target, behavior);
    this.#releasePinnedTargetAfterScroll(target, name);
  }

  #getStickyOffset() {
    if (this.#cachedStickyOffset !== null) {
      return this.#cachedStickyOffset;
    }

    const rootStyles = getComputedStyle(document.documentElement);
    const headerOffset = parseFloat(rootStyles.getPropertyValue('--sticky-area-height')) || 0;
    const linklistSection = this.closest('.shopify-section--linklist');
    let linklistSticky = 0;

    if (linklistSection) {
      const sectionStyles = getComputedStyle(linklistSection);
      const isSticky = parseInt(sectionStyles.getPropertyValue('--linklist-is-sticky').trim(), 10) === 1;

      if (isSticky) {
        linklistSticky = parseFloat(rootStyles.getPropertyValue('--linklist-height')) || 0;
      }
    }

    this.#cachedStickyOffset = headerOffset + linklistSticky;
    return this.#cachedStickyOffset;
  }

  #pickActiveTarget() {
    if (this.#pinnedTarget) {
      return this.#pinnedTarget;
    }

    if (this.#scrollIntentName && performance.now() < this.#scrollIntentUntil) {
      return this.#scrollIntentName;
    }

    const activationLine = this.#getStickyOffset();
    const viewportBottom = window.innerHeight;
    let activeName = null;
    let maxVisibleHeight = 0;
    let closestTopDistance = Infinity;

    for (const [name, target] of this.#targetByName) {
      const rect = target.getBoundingClientRect();
      const visibleTop = Math.max(rect.top, activationLine);
      const visibleBottom = Math.min(rect.bottom, viewportBottom);
      const visibleHeight = Math.max(0, visibleBottom - visibleTop);

      if (visibleHeight === 0) {
        continue;
      }

      const topDistance = Math.abs(rect.top - activationLine);

      if (
        visibleHeight > maxVisibleHeight ||
        (visibleHeight === maxVisibleHeight && topDistance < closestTopDistance)
      ) {
        maxVisibleHeight = visibleHeight;
        closestTopDistance = topDistance;
        activeName = name;
      }
    }

    return activeName ?? this.#targetByName.keys().next().value ?? null;
  }

  #scrollToTarget(target, behavior) {
    const offset = this.#getStickyOffset();
    const top = window.scrollY + target.getBoundingClientRect().top - offset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior,
    });
  }

  #scheduleUpdateActive() {
    if (this.#pinnedTarget) {
      return;
    }

    if (this.#pendingActiveUpdate) {
      return;
    }

    this.#pendingActiveUpdate = requestAnimationFrame(() => {
      this.#pendingActiveUpdate = null;
      this.#updateActive();
    });
  }

  #scrollActiveLinkIntoView(activeLink) {
    const scrollContainer = activeLink.closest('.scroll-area');

    if (!scrollContainer) {
      return;
    }

    const targetScrollLeft =
      activeLink.offsetLeft - (scrollContainer.clientWidth - activeLink.offsetWidth) / 2;

    scrollContainer.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: window.matchMedia('(prefers-reduced-motion: no-preference)').matches ? 'smooth' : 'auto',
    });
  }

  #setActive(name, { scrollLink = false, force = false } = {}) {
    const normalizedName = this.#normalizeAnchorName(name);

    if (!force && normalizedName === this.#activeTarget) {
      return;
    }

    this.#activeTarget = normalizedName || null;

    for (const link of this.#getAllAnchorLinks()) {
      const isActive = this.#normalizeAnchorName(link.dataset.linklistAnchor) === normalizedName;
      const isDesktopTab = Boolean(link.closest('.linklist-menu__desktop'));

      link.classList.toggle('link-faded', !isActive);
      link.classList.toggle('linklist-menu__option--active', isActive && !isDesktopTab);

      if (isDesktopTab) {
        link.setAttribute('aria-current', isActive ? 'true' : 'false');
        link.setAttribute('aria-selected', isActive ? 'true' : 'false');
      } else {
        link.removeAttribute('aria-current');
        link.removeAttribute('aria-selected');
      }
    }

    this.#updateTabIndices(normalizedName);

    const activeLink =
      normalizedName ?
        this.#getAllAnchorLinks().find(
          (link) => this.#normalizeAnchorName(link.dataset.linklistAnchor) === normalizedName
        )
      : null;
    const toggleLabel = this.querySelector('[data-linklist-toggle-label]');

    if (toggleLabel && activeLink) {
      toggleLabel.textContent = activeLink.textContent.trim();
    }

    if (scrollLink && activeLink?.closest('.linklist-menu__desktop')) {
      this.#scrollActiveLinkIntoView(activeLink);
    }
  }

  #updateTabIndices(activeName) {
    const tabs = this.querySelectorAll('.linklist-menu__desktop a[data-linklist-anchor]');
    const normalizedName = this.#normalizeAnchorName(activeName);

    for (const tab of tabs) {
      const isActive = this.#normalizeAnchorName(tab.dataset.linklistAnchor) === normalizedName;
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }
  }

  #updateActive() {
    this.#setActive(this.#pickActiveTarget());
  }

  #initKeyboardNavigation() {
    const tablist = this.querySelector('.linklist-menu__desktop[role="tablist"]');

    if (!tablist) {
      return;
    }

    this.#tablistKeydownListener = (event) => this.#onTablistKeydown(event);
    tablist.addEventListener('keydown', this.#tablistKeydownListener);
  }

  #teardownKeyboardNavigation() {
    const tablist = this.querySelector('.linklist-menu__desktop[role="tablist"]');

    if (tablist && this.#tablistKeydownListener) {
      tablist.removeEventListener('keydown', this.#tablistKeydownListener);
      this.#tablistKeydownListener = null;
    }
  }

  #onTablistKeydown(event) {
    const tabs = [...this.querySelectorAll('.linklist-menu__desktop a[data-linklist-anchor]')];

    if (!tabs.length) {
      return;
    }

    const currentIndex = tabs.indexOf(document.activeElement);
    let nextIndex = -1;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowLeft':
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    tabs[nextIndex].focus();
  }

  #pinTarget(name) {
    this.#clearPinnedTarget();
    this.#pinnedTarget = this.#normalizeAnchorName(name);
  }

  #clearPinnedTarget() {
    this.#pinnedTarget = null;

    if (this.#pinReleaseTimer) {
      window.clearTimeout(this.#pinReleaseTimer);
      this.#pinReleaseTimer = null;
    }

    if (this.#scrollSettleFrame) {
      cancelAnimationFrame(this.#scrollSettleFrame);
      this.#scrollSettleFrame = null;
    }
  }

  #setScrollIntent(name) {
    this.#scrollIntentName = this.#normalizeAnchorName(name);
    this.#scrollIntentUntil = performance.now() + 800;
  }

  #clearScrollIntent() {
    this.#scrollIntentName = null;
    this.#scrollIntentUntil = 0;
  }

  #releasePinnedTargetAfterScroll(target, intentName, { focusToggle = false } = {}) {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let released = false;

    const release = () => {
      if (released) {
        return;
      }

      released = true;

      if (this.#scrollSettleFrame) {
        cancelAnimationFrame(this.#scrollSettleFrame);
        this.#scrollSettleFrame = null;
      }

      if (this.#pinReleaseTimer) {
        window.clearTimeout(this.#pinReleaseTimer);
        this.#pinReleaseTimer = null;
      }

      requestAnimationFrame(() => {
        this.#clearPinnedTarget();

        if (intentName) {
          this.#setActive(intentName, { force: true });
        } else {
          this.#updateActive();
        }

        if (focusToggle) {
          this.querySelector('.linklist-menu__toggle')?.focus();
        }
      });
    };

    if (prefersReducedMotion) {
      requestAnimationFrame(() => requestAnimationFrame(release));
      return;
    }

    const offset = this.#getStickyOffset();
    let lastScrollY = window.scrollY;
    let stableFrames = 0;
    let frameCount = 0;
    const maxFrames = 90;

    const waitForScrollSettle = () => {
      if (released) {
        return;
      }

      frameCount += 1;
      const rect = target.getBoundingClientRect();
      const aligned = Math.abs(rect.top - offset) <= 12;
      const scrollY = window.scrollY;

      if (scrollY === lastScrollY) {
        stableFrames += 1;
      } else {
        stableFrames = 0;
        lastScrollY = scrollY;
      }

      if ((stableFrames >= 2 && aligned) || frameCount >= maxFrames) {
        release();
        return;
      }

      this.#scrollSettleFrame = requestAnimationFrame(waitForScrollSettle);
    };

    this.#pinReleaseTimer = window.setTimeout(release, 1500);
    this.#scrollSettleFrame = requestAnimationFrame(waitForScrollSettle);
  }

  #onLinkClick = async (event) => {
    const link = event.target.closest('a[href]');
    const popover = this.#getPopover();

    if (!link || (!this.contains(link) && !popover?.contains(link))) {
      return;
    }

    const url = new URL(link.href, window.location.href);
    const inPopover = Boolean(link.closest('x-popover'));
    const hasHash = url.hash && url.hash !== '#';

    if (inPopover) {
      await this.#closePopover(link);
    }

    if (!hasHash) {
      return;
    }

    const target = this.#findScrollTarget(url.hash);

    if (!target) {
      return;
    }

    event.preventDefault();

    const anchorName = this.#normalizeAnchorName(decodeURIComponent(url.hash.slice(1)));

    if (anchorName) {
      this.#setScrollIntent(anchorName);
      this.#pinTarget(anchorName);
      this.#setActive(anchorName, { scrollLink: !inPopover, force: true });
    }

    const behavior = window.matchMedia('(prefers-reduced-motion: no-preference)').matches ? 'smooth' : 'auto';

    this.#scrollToTarget(target, behavior);

    const samePage = url.pathname === window.location.pathname && url.search === window.location.search;

    if (samePage) {
      history.pushState(null, '', `${url.pathname}${url.search}${url.hash}`);
      document.documentElement.dispatchEvent(
        new CustomEvent('hashchange:simulate', { bubbles: true, detail: { hash: url.hash } })
      );
    }

    this.#releasePinnedTargetAfterScroll(target, anchorName, { focusToggle: inPopover });
  };

  #closePopover(link) {
    const popover = link.closest('x-popover');

    if (typeof popover?.hide === 'function') {
      return popover.hide();
    }

    return Promise.resolve();
  }
}

if (!window.customElements.get('linklist-menu')) {
  window.customElements.define('linklist-menu', LinklistMenu);
}
